/**
 * WorkflowSectionPage — section shell for all queue-based workflows.
 *
 * Layout: sidebar nav (Overview | Queue | Activity) + content area.
 * Activity navigates to a standalone full-page view.
 * Queue embeds WorkflowPage with the 3-tab agent workspace.
 */

import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Page }                      from '@shopify/polaris'
import { WorkflowPage }              from './WorkflowPage'
import { WorkflowSectionOverview }   from './WorkflowSectionOverview'
import { WorkspaceActionBar }        from './WorkspaceActionBar'
import { WorkflowHistoryPage }       from '../../pages/boarding/history/WorkflowHistoryPage'
import { DeliveryTrackingActivityPage } from '../../modules/dt-dt/pages/DeliveryTrackingActivityPage'
import { useAppSession }             from '../../hooks/useAppSession'
import { getShop }                   from '../../sdk/shop'
import type { WorkflowId }           from '../../sdk/engine.api'

type WorkflowHistoryParam = 'cf' | 'sc' | 'st' | 'cs'

type SectionConfig = {
  title:             string
  basePath:          string
  workflowOverride?: WorkflowHistoryParam
}

const SECTIONS: Record<string, SectionConfig> = {
  confirmation:          { title: 'Confirmation',          basePath: '/workspace/confirmation',          workflowOverride: 'cf' },
  shipping_confirmation: { title: 'Shipping Confirmation', basePath: '/workspace/shipping-confirmation', workflowOverride: 'sc' },
  sales:                 { title: 'Sales Team',            basePath: '/workspace/sales-team',            workflowOverride: 'st' },
  customer_service:      { title: 'Customer Service',      basePath: '/workspace/customer-service',      workflowOverride: 'cs' },
  delivery_tracking:     { title: 'Delivery Tracking',     basePath: '/workspace/delivery-tracking' },
}

const NAV_ITEM: React.CSSProperties = {
  display:    'block',
  width:      '100%',
  textAlign:  'left',
  padding:    '10px 20px',
  border:     'none',
  background: 'transparent',
  fontSize:   14,
  cursor:     'pointer',
  fontWeight: 400,
  color:      '#6d7175',
  borderLeft: '3px solid transparent',
}

const NAV_ITEM_ACTIVE: React.CSSProperties = {
  ...NAV_ITEM,
  background:  '#f1f2f3',
  color:       '#202223',
  fontWeight:  600,
  borderLeft:  '3px solid #2c6ecb',
}

type Props = { workflowId: WorkflowId }

export function WorkflowSectionPage({ workflowId }: Props) {
  const { session }  = useAppSession()
  const employeeId   = session?.employeeId ?? ''
  const { pathname } = useLocation()
  const navigate     = useNavigate()
  const config       = SECTIONS[workflowId]!

  const isActivity = pathname.endsWith('/activity')
  const isQueue    = pathname.endsWith('/queue')

  // Owners and admins see Overview + Activity only — never enter queue distribution
  const isAdminOrOwner = session?.tenantRole === 'owner' || session?.tenantRole === 'admin'

  // Admins/owners blocked from queue
  useEffect(() => {
    if (isQueue && isAdminOrOwner) {
      navigate(config.basePath, { replace: true })
    }
  }, [isQueue, isAdminOrOwner, navigate, config.basePath])

  // G-11: Non-admin agents go directly to their queue — Overview is manager-only
  useEffect(() => {
    if (!isQueue && !isActivity && !isAdminOrOwner && session !== undefined) {
      navigate(`${config.basePath}/queue`, { replace: true })
    }
  }, [isQueue, isActivity, isAdminOrOwner, session, navigate, config.basePath])

  // G-6/G-9: signal to refresh WorkflowPage queue after Pull More
  const [pullSignal, setPullSignal] = useState(0)

  // Activity is a full standalone page (has its own Page wrapper + back button)
  if (isActivity) {
    return config.workflowOverride
      ? <WorkflowHistoryPage workflowOverride={config.workflowOverride} backUrl={config.basePath} />
      : <DeliveryTrackingActivityPage />
  }

  const activeId = isQueue && !isAdminOrOwner ? 'queue' : 'overview'

  // Admins/owners: Overview + Activity. Agents: Queue + Activity.
  const navItems = [
    ...(isAdminOrOwner  ? [{ id: 'overview', label: 'Overview', path: config.basePath }]              : []),
    ...(!isAdminOrOwner ? [{ id: 'queue',    label: 'Queue',    path: `${config.basePath}/queue` }]   : []),
    // G9: Customer Service prepares order-request tickets (returns/exchanges/new).
    ...(workflowId === 'customer_service' ? [{ id: 'requests', label: 'Order Requests', path: '/dt-cs/requests' }] : []),
    { id: 'activity', label: 'Activity', path: `${config.basePath}/activity` },
  ]

  return (
    <Page title={config.title} backAction={{ content: 'Workspace', url: '/workspace-hub' }}>
      <div style={{ display: 'flex', minHeight: 520, marginTop: 8 }}>

        {/* Sidebar nav */}
        <div style={{
          width: 180, flexShrink: 0,
          borderRight: '1px solid #e1e3e5',
          paddingTop: 4,
        }}>
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              style={activeId === item.id ? NAV_ITEM_ACTIVE : NAV_ITEM}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Content area */}
        <div style={{ flex: 1, paddingLeft: 24, paddingTop: 4, minWidth: 0 }}>
          {activeId === 'overview' && (
            <WorkflowSectionOverview
              workflowId={workflowId}
              employeeId={employeeId}
              onViewQueue={() => navigate(`${config.basePath}/queue`)}
            />
          )}
          {activeId === 'queue' && (
            <div>
              <WorkspaceActionBar
                workflowId={workflowId}
                shop={getShop() ?? ''}
                employeeName={session?.displayName ?? 'You'}
                onPulled={() => setPullSignal(s => s + 1)}
                onCheckedOut={() => navigate(config.basePath)}
              />
              <div style={{ paddingTop: 8 }}>
                <WorkflowPage
                  workflowId={workflowId}
                  embedded
                  agentFilter
                  refreshSignal={pullSignal}
                />
              </div>
            </div>
          )}
        </div>

      </div>
    </Page>
  )
}
